import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { useAuth } from "../contexts/AuthContext";
import { usePageConfig } from "../contexts/PageConfigContext";
import { AppSidebar } from "./AppSidebar";
import { Header } from "./Header";
import { NotificationBell } from "./NotificationBell";
import { UserNav } from "./UserNav";
import { TopBar } from "./TopBar";
import { BottomBar } from "./BottomBar";

import { settingsPageConfig } from "../pages/settings";
import { ArrowLeft, LogOut } from "lucide-react";
import { PullToRefresh } from "./PullToRefresh";
import { Dialog } from "./Dialog";
import {
  SidebarProvider,
  SidebarInset,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { NavigationTabs } from "./NavigationTabs";

export const Layout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { pageConfig, setPageConfig } = usePageConfig();
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);

  const handleLogoutClick = () => {
    setIsLogoutConfirmOpen(true);
  };

  const handleLogoutConfirm = () => {
    logout();
    setIsLogoutConfirmOpen(false);
  };

  // Determine current page config based on route
  useEffect(() => {
    const path = location.pathname;

    if (path.startsWith("/comandas")) {
      if (path === "/comandas/nueva") {
        setPageConfig({
          title: "Nueva Venta",
          description: "Completa los datos del pedido",
          actions: [
            {
              label: "Volver",
              icon: ArrowLeft,
              onClick: () => navigate("/comandas"),
              variant: "secondary",
            },
          ],
        });
      } else if (path.startsWith("/comandas/editar")) {
        setPageConfig({
          title: "Editar Venta",
          description: "Modifica los datos del pedido",
          actions: [
            {
              label: "Volver",
              icon: ArrowLeft,
              onClick: () => navigate("/comandas"),
              variant: "secondary",
            },
          ],
        });
      }
    } else if (path === "/configuracion") {
      setPageConfig(settingsPageConfig);
    }
  }, [location.pathname, navigate, setPageConfig]);

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-slate-100 flex w-full print:block print:bg-white">
        <AppSidebar onLogout={handleLogoutClick} />
        <SidebarInset className="flex flex-col w-full print:p-0">
          {/* Desktop Header: Navigation */}
          <div className="hidden md:block print:hidden">
            <Header onLogout={handleLogoutClick} />
          </div>

          {/* Mobile Header (inline) */}
          <div className="md:hidden sticky top-0 z-30 bg-white border-b border-slate-200 print:hidden">
            <div className="flex items-center h-14 px-4 gap-4 relative justify-between">
              <SidebarTrigger />
              <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <img
                  src="/images/logo.png"
                  alt="El Mozuelo"
                  className="h-9 w-auto object-contain"
                />
              </div>
              <div className="ml-auto flex items-center gap-3">
                <NotificationBell variant="header" />
                <UserNav onLogout={handleLogoutClick} />
              </div>
            </div>
            <div className="px-4 pb-2">
              <NavigationTabs className="py-2" />
            </div>
          </div>

          <main className="flex-grow w-full pb-20 md:pb-8 p-4 md:p-8 print:p-0 print:m-0">
            <PullToRefresh>
              <div className="mx-auto">
                {/* Secondary Header: Page Context & Actions */}
                <TopBar config={pageConfig} />

                <Outlet />
              </div>
            </PullToRefresh>
          </main>

          <BottomBar />
        </SidebarInset>
      </div>

      <Dialog
        isOpen={isLogoutConfirmOpen}
        onClose={() => setIsLogoutConfirmOpen(false)}
        title="Cerrar Sesión"
        size="sm"
      >
        <div className="flex flex-col gap-6">
          <p className="text-slate-600">
            ¿Estás seguro de que quieres cerrar sesión? Tendrás que volver a
            ingresar tus credenciales para acceder.
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsLogoutConfirmOpen(false)}
              className="px-4 py-2 text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors font-medium"
            >
              Cancelar
            </button>
            <button
              onClick={handleLogoutConfirm}
              className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors font-medium flex items-center gap-2"
            >
              <LogOut size={16} />
              Cerrar Sesión
            </button>
          </div>
        </div>
      </Dialog>
    </SidebarProvider>
  );
};
